/**
 * As peças de que a coluna ao lado da grade é feita.
 *
 * Papel: aqui ficam `SecaoDoPainel` — o título mono em caixa alta sobre um
 * filete, que abre cada bloco da coluna —, `ListaDoPainel`, a lista que vira
 * frase quando está vazia ou quando a consulta falhou, `LinhaDoPainel`, a
 * faixa cinza de cada pessoa ou
 * período, `LinhaDeAcrescentar`, o campo com o "+" que põe um nome na lista, e
 * as classes dos botões pequenos de linha em `BOTAO_DE_LINHA`.
 *
 * Como usar: `ListaDeFuncionarios`, `PainelPeriodos` e `AcoesDoMes` montam os
 * seus blocos só com estas peças, e é o que faz as quatro seções da coluna
 * terem o mesmo espaçamento. Cada `SecaoDoPainel` depois da primeira abre 18 px
 * abaixo da anterior pela própria margem, e essa margem se funde com a margem
 * de baixo do último item da seção anterior — por isso a coluna deverá
 * continuar em fluxo de bloco. Montada dentro de um `flex` ou de um `grid`, a
 * fusão deixa de acontecer e o espaço entre as seções dobra.
 */
import { type FormEvent, type ReactNode, useId, useState } from 'react';

import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';

/**
 * As classes dos botões "✎" e "×" de uma linha, mais baixos que o `Button`
 * pequeno. Vão junto de `size="xs"` e da variante: `outline` para editar,
 * `destructive` para remover.
 */
export const BOTAO_DE_LINHA = 'px-2 py-0.5 text-[11px]';

/**
 * Um bloco da coluna, com o título que o nomeia.
 *
 * @param titulo - O texto do título, que o CSS põe em caixa alta; é também o
 *   nome acessível do bloco.
 * @param children - O que vem abaixo do título: a lista, o formulário e o botão.
 * @returns A seção com o título sobre o filete.
 */
export function SecaoDoPainel({ titulo, children }: { titulo: string; children: ReactNode }) {
  const idDoTitulo = useId();

  return (
    <section aria-labelledby={idDoTitulo} className="not-first:mt-[18px]">
      <h2
        id={idDoTitulo}
        className="mb-2.5 border-b border-linha-forte pb-1.5 font-mono text-[10px] font-semibold tracking-[0.08em] text-texto-fraco uppercase"
      >
        {titulo}
      </h2>
      {children}
    </section>
  );
}

/**
 * A faixa de uma pessoa ou de um período.
 *
 * @param alinharNoTopo - `true` na linha de duas alturas — o nome sobre as
 *   datas —, para os botões ficarem na altura do nome e não no meio da faixa.
 * @param children - O conteúdo da faixa, da esquerda para a direita.
 * @returns O item de lista.
 */
export function LinhaDoPainel({
  alinharNoTopo = false,
  children,
}: {
  alinharNoTopo?: boolean;
  children: ReactNode;
}) {
  return (
    <li
      className={cn(
        'flex gap-2 rounded-[2px] border border-linha bg-superficie-2 px-2 py-1.5 text-[12.5px]',
        alinharNoTopo ? 'items-start' : 'items-center',
      )}
    >
      {children}
    </li>
  );
}

/**
 * A lista de faixas, ou a frase de lista vazia quando não há nenhuma.
 *
 * @param vazia - Se não há faixa nenhuma para mostrar.
 * @param mensagemVazia - A frase que ocupa o lugar da lista vazia.
 * @param erro - A frase de falha da consulta que alimenta a lista; presente,
 *   ela ocupa o lugar da lista e da frase de vazia, porque uma consulta que
 *   falhou não disse que o mês está vazio.
 * @param children - As `LinhaDoPainel` da lista.
 * @returns A lista, a frase de vazia ou a de falha, com a mesma margem de baixo.
 */
export function ListaDoPainel({
  vazia,
  mensagemVazia,
  erro,
  children,
}: {
  vazia: boolean;
  mensagemVazia: string;
  erro?: string;
  children: ReactNode;
}) {
  if (erro !== undefined) {
    return (
      <p role="alert" className="mb-2.5 py-1 text-xs text-perigo">
        {erro}
      </p>
    );
  }

  if (vazia) {
    return <p className="mb-2.5 py-1 text-xs text-texto-fraco">{mensagemVazia}</p>;
  }

  return <ul className="mb-2.5 flex list-none flex-col gap-1 p-0">{children}</ul>;
}

/**
 * O campo de acrescentar um nome à lista, com o "+" ao lado.
 *
 * O texto vai aparado para `onAcrescentar`, e o campo só se esvazia quando ela
 * devolve `null`; a frase que ela devolver no lugar aparece debaixo do campo, e
 * o texto continua nele para ser corrigido. Campo vazio não chama nada.
 *
 * @param placeholder - O texto do campo vazio, que também é o nome acessível.
 * @param sugestoes - Os nomes oferecidos enquanto se digita; vazio, o campo não
 *   sugere nada.
 * @param onAcrescentar - Recebe o nome digitado e devolve `null` quando ele
 *   entrou na lista, ou a frase do motivo de não ter entrado.
 * @returns O formulário da linha, com a frase de recusa quando houver.
 */
export function LinhaDeAcrescentar({
  placeholder,
  sugestoes = [],
  onAcrescentar,
}: {
  placeholder: string;
  sugestoes?: readonly string[];
  onAcrescentar: (nome: string) => Promise<string | null>;
}) {
  const idDasSugestoes = useId();
  const [texto, setTexto] = useState('');
  const [recusa, setRecusa] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  /** Manda o nome digitado e limpa o campo quando ele entra na lista. */
  async function handleAcrescentar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    const nome = texto.trim();
    if (nome === '') return;

    setEnviando(true);
    setRecusa(null);
    try {
      const motivo = await onAcrescentar(nome);
      if (motivo === null) setTexto('');
      else setRecusa(motivo);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form onSubmit={(evento) => void handleAcrescentar(evento)} className="mb-3.5">
      <div className="flex gap-1.5">
        <Input
          aria-label={placeholder}
          placeholder={placeholder}
          list={sugestoes.length > 0 ? idDasSugestoes : undefined}
          value={texto}
          onChange={(evento) => setTexto(evento.target.value)}
          disabled={enviando}
          className="min-w-0 flex-1 rounded-[2px] bg-superficie px-2.5 py-[7px] text-[13px]"
        />
        <Button type="submit" disabled={enviando} className="px-3.5 py-2">
          +
        </Button>
      </div>
      {sugestoes.length > 0 && (
        <datalist id={idDasSugestoes}>
          {sugestoes.map((nome) => (
            <option key={nome} value={nome} />
          ))}
        </datalist>
      )}
      {recusa !== null && (
        <p role="alert" className="mt-1 text-xs text-perigo">
          {recusa}
        </p>
      )}
    </form>
  );
}
